import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useState, forwardRef } from 'react';
import { AmazonConnector } from '@emburse/embark-complex';
import { alertToast, confirmToast } from '../../util/toast';
import { $t, useTranslation } from '../../hooks/useTranslation';
import { GetConnectionStatus, GetAuthGrantUrl, Disconnect } from '../../api/amazonBusiness';
import { parseData } from '../../api/common';
import { logger } from '../../util/logger';
import useAccountConfig from '../../hooks/useAccountConfig';
var ConnectionState;
(function (ConnectionState) {
    ConnectionState["Loading"] = "loading";
    ConnectionState["Disconnected"] = "disconnected";
    ConnectionState["Connected"] = "connected";
    ConnectionState["Error"] = "error";
})(ConnectionState || (ConnectionState = {}));
const RESP_STATUS_TO_CONNECTION_STATE = {
    'not-connected': ConnectionState.Disconnected,
    connected: ConnectionState.Connected,
    error: ConnectionState.Error
};
const convertConnectionResponseToConnectionState = (response) => {
    const responseData = JSON.parse(response.Data || '{}');
    return RESP_STATUS_TO_CONNECTION_STATE[responseData.status];
};
const resolveApiResponse = async (callback) => {
    const response = await callback()
        .then(parseData)
        .catch((err) => logger.error(err.toString()));
    if (!response || response.Status !== 'success') {
        return Promise.reject(response);
    }
    return Promise.resolve(response);
};
const getConnectionStatus = async () => resolveApiResponse(GetConnectionStatus)
    .then(convertConnectionResponseToConnectionState)
    .catch(() => ConnectionState.Error);
const CertifyAmazonConnector = forwardRef((props, ref) => {
    const { accountConfig } = useAccountConfig();
    useTranslation();
    const [connectionState, setConnectionState] = useState(ConnectionState.Loading);
    const refreshConnectionState = useCallback(async () => {
        const newConnectionState = await getConnectionStatus();
        setConnectionState(newConnectionState);
        return newConnectionState;
    }, []);
    const connectCallback = useCallback(async () => {
        setConnectionState(ConnectionState.Loading);
        try {
            const response = await resolveApiResponse(GetAuthGrantUrl);
            const { url } = JSON.parse(response.Data);
            window.location.assign(url);
        }
        catch (error) {
            if (error.Message) {
                alertToast.show(error.Message);
            }
            refreshConnectionState();
        }
    }, [refreshConnectionState]);
    const disconnectCallback = useCallback(async () => {
        setConnectionState(ConnectionState.Loading);
        try {
            const disconnectResponse = await resolveApiResponse(Disconnect);
            const redirectUrl = disconnectResponse.Data;
            if (accountConfig.UIFlags.EnableNewAmazonText && redirectUrl.length) {
                window.location.assign(redirectUrl);
            }
            confirmToast.show(`Amazon Business ${$t('account disconnected successsfully')}`);
        }
        catch (error) {
            if (error.Message) {
                alertToast.show(error.Message);
            }
        }
        finally {
            refreshConnectionState();
        }
    }, [refreshConnectionState, accountConfig.UIFlags.EnableNewAmazonText]);
    const initialize = useCallback(async () => {
        const initConnectionState = await refreshConnectionState();
        if (initConnectionState !== ConnectionState.Error) {
            const params = new URLSearchParams(window.location.search);
            const status = params.get('status');
            if (status === 'connected') {
                window.addEventListener('load', async () => {
                    confirmToast.show(`Amazon Business ${$t('account connected successfully')}`);
                    window.history.replaceState(null, document.title, 'AmazonBusiness.aspx');
                });
            }
        }
    }, [refreshConnectionState]);
    useEffect(() => {
        initialize();
    }, [initialize]);
    return (_jsx(AmazonConnector, { connectionState: connectionState, connectCallback: connectCallback, disconnectCallback: disconnectCallback, ref: ref, newAmazonTextFlag: accountConfig.UIFlags.EnableNewAmazonText }));
});
export default CertifyAmazonConnector;
